import { TCrossLinksGroup } from '@/screens/Product/hooks/useProduct'

import { nextfetch, nextfetchdata } from '@/shared/api/nextfetch'
import { filterAvailableProducts } from '@/shared/lib/utils/url/filterAvailableProducts.utils'
import { TProduct } from '@/shared/types/product-types'

export type TGetProductsResponseData = {
	IS_LAST_PAGE?: boolean
	ITEMS: TProduct[]
	CURRENT_PAGE: number
	LAST_PAGE: number
	PER_PAGE: number
	TOTAL: number
}

export interface ContactData {
	NAME: string
	PHONE: string
	EMAIL?: string | null
}

export type TNpiFormFields = {
	KOD: string | undefined
} & ContactData

const nextFetchConfig = { next: { revalidate: 60 } }

export const fetchProduct = (
	{ productCode = ``, cookie = `` },
	_throwError: boolean = false
): Promise<TProduct | void> =>
	nextfetchdata<TProduct>(`products/${productCode}`, {
		cookie,
		credentials: `include`,
		...nextFetchConfig
	})

export const fetchCrossLinksProducts = (productCode: string): Promise<TCrossLinksGroup[] | void> =>
	nextfetchdata<TCrossLinksGroup[]>(`products/${productCode}/crosslinks`, nextFetchConfig)

export const fetchProducts = (
	parameters: URLSearchParams,
	filterUrl = ``,
	regionCookies: any
): Promise<TGetProductsResponseData | void> =>
	nextfetchdata<TGetProductsResponseData>(`index/catalog/products?${parameters}&filter=${filterUrl}&limit=32`, {
		cookie: regionCookies
	})

export const fetchProductsByPropValues = async (
	propName: string,
	values: (string | number)[],
	withDefaultFilters: boolean = true
): Promise<TProduct[] | void> => {
	const getParams = new URLSearchParams(values.map(value => `filter[${propName}][]=${value}`).join(`&`)).toString()

	return nextfetchdata<TGetProductsResponseData>(`products?${getParams}`, {
		params: {
			default_filter: String(withDefaultFilters)
		}
	})
		.then(res => {
			if (res) return res.ITEMS
		})
		.catch(() => {
			return []
		})
}

export const fetchProductsByCodes = async (
	productsCodes: string[],
	withDefaultFilters: boolean = true
): Promise<TProduct[] | void> => {
	if (!productsCodes.length) return []

	// Через index/catalog (по указанию бэкенда): только этот эндпоинт отдаёт поле CONTROL,
	// от которого зависит скрытие цены/кнопок в карточке товара.
	const filter = {
		KOD: {
			type: '=',
			value: productsCodes
		}
	}
	// Сырой JSON: nextfetch сам кодирует params через URLSearchParams.
	// encodeURIComponent тут дал бы двойное кодирование и 400 от бэка.
	return nextfetchdata<TGetProductsResponseData>(`index/catalog/products`, {
		params: {
			filter: JSON.stringify(filter),
			limit: productsCodes.length,
			default_filter: String(withDefaultFilters)
		}
	})
		.then(res => {
			if (res) return res.ITEMS
		})
		.catch(() => {
			return []
		})
}

export const fetchPromoProducts = async (productsCodes: string[]): Promise<TProduct[] | []> => {
	const filter = {
		KOD: {
			type: '=',
			value: productsCodes // список кодов продуктов
		}
	}

	const filterParam = encodeURIComponent(JSON.stringify(filter))

	return nextfetchdata<TGetProductsResponseData>(`index/catalog/products`, {
		params: { filter: filterParam, limit: 48 }
	}).then(res => {
		if (res) return res.ITEMS.filter(filterAvailableProducts) // фильтрация доступных товаров
	})
}

export const fetchFullProductsInfo = async (productsCodes: string[]): Promise<TProduct[]> => {
	const products = await Promise.all(productsCodes.map(productCode => fetchProduct({ productCode })))

	return products.filter(Boolean) as TProduct[]
}

export const fetchProductById = (id: number): Promise<TGetProductsResponseData | void> =>
	nextfetchdata<TGetProductsResponseData>(`index/catalog/products?filter={"ID":{"type":"=","value":["${id}"]}}`)

export const fetchReservation = async (formData: TNpiFormFields): Promise<void> =>
	await nextfetch<void>(`reservation/product/send`, {
		method: 'POST',
		params: {
			...formData
		}
	})
